import React, { useState, useEffect, useCallback } from 'react';
import { FiChevronRight, FiCreditCard, FiMail } from 'react-icons/fi';
import { BsDiagram3 } from 'react-icons/bs'; // Icon for splits
import { Transaction, TagMap } from '../types'; // Import TagMap
import TransactionWithNarration from './TransactionWithNarration'; // Import the new component
import { ReactComponent as ExcludedIcon } from '../assets/icons/ExcludedFromAccountingIcon.svg'; // Import the custom SVG

// Import Redux hooks and actions
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store'; // Correct path based on search
import { updateTransactionAsync } from '../store/slices/transactionsSlice';
import { useNavigation } from '../hooks/useNavigation';

interface TransactionDetailViewProps {
    transaction: Transaction;
    tagMap: TagMap; // Use the correct type
    // Placeholder functions for actions - implementation later
    onEdit?: (transaction: Transaction) => void;
    onSplit?: (transaction: Transaction) => void;
    onTagClick?: (transaction: Transaction, event: React.MouseEvent) => void;
    onSourceEmailClick?: (transaction: Transaction) => void;
    // Optional: Add account details if needed
    // account?: Account; // Assuming an Account type exists
    onManageSplit?: (transaction: Transaction) => void; // Prop to handle opening the split manager
}

function TransactionDetailView({
    transaction,
    tagMap,
    onTagClick,
    onSourceEmailClick,
    onManageSplit, // Destructure the new prop
}: TransactionDetailViewProps) {
    const [isExcluded, setIsExcluded] = useState(transaction.excludeFromAccounting || false);
    const [note, setNote] = useState(transaction.notes || ''); // Use 'notes' based on model change
    const isCashAccountTransaction = transaction.account?.name === 'Cash';
    const { navigateTo } = useNavigation();

    // Get the dispatch function
    const dispatch = useDispatch<AppDispatch>();

    const toggleExclude = async () => {
        if (!transaction?.id) {
            console.error('Cannot update excludeFromAccounting: transaction ID is missing');
            return; // Exit if no transaction ID
        }

        const newExcludedValue = !isExcluded;
        setIsExcluded(newExcludedValue);

        // Dispatch the update action
        try {
            console.log('Dispatching updateTransactionAsync for excludeFromAccounting:', newExcludedValue);
            await dispatch(updateTransactionAsync({ transactionId: transaction.id, updatedData: { excludeFromAccounting: newExcludedValue } })).unwrap();
            console.log('excludeFromAccounting update dispatched successfully');
        } catch (error) {
            console.error('Failed to dispatch excludeFromAccounting update:', error);
            // Optional: revert the UI state if the server update fails
            setIsExcluded(!newExcludedValue);
            // TODO: Add user feedback for save failure
        }
    };

    const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNote(event.target.value);
    };

    const openAccountDetails = useCallback(() => {
        if (!transaction.account) {
            return;
        }

        navigateTo(`/accounts/${transaction.account.id}`, {
            state: {
                account: transaction.account,
                isChild: transaction.account.parentAccountId != null,
            },
        });
    }, [navigateTo, transaction.account]);

    useEffect(() => {
        if (!transaction.id || note === (transaction.notes || '')) {
            return;
        }
        const timer = setTimeout(async () => {
            try {
                await dispatch(updateTransactionAsync({
                    transactionId: transaction.id, updatedData: { notes: note },
                })).unwrap();
            } catch (error) {
                console.error('Failed to dispatch note update:', error);
            }
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [note, transaction.id, transaction.notes, dispatch]);

    useEffect(() => {
        setIsExcluded(transaction.excludeFromAccounting || false);
    }, [transaction.excludeFromAccounting]);

    // Basic structure - adjust styling and layout based on your image/design
    return (
        <div className="p-4 pt-2 flex flex-col h-full text-foreground bg-background overflow-y-auto gap-4">

            {/* Header Section */}
            {transaction && (
                <TransactionWithNarration
                    transaction={transaction}
                    tagMap={tagMap}
                    onTagClick={onTagClick}
                    disableBottomMargin={true}
                />
            )}

            {/* Account Chip Section */}
            {transaction.account && (
                <button
                    type="button"
                    onClick={openAccountDetails}
                    className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-left"
                >
                    <div className="flex items-center justify-between gap-3 text-foreground">
                        <div className="flex items-center gap-3">
                            <FiCreditCard className="h-4 w-4 text-muted-foreground" />
                            <span className="text-base font-semibold">Account</span>
                        </div>
                        <div className="inline-flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm font-medium">{transaction.account.name}</span>
                            {!isCashAccountTransaction && (
                                <span className="shrink-0 text-sm text-muted-foreground">{transaction.account.accountNumber.slice(-4)}</span>
                            )}
                            <FiChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                        </div>
                    </div>
                </button>
            )}

            <button
                type="button"
                className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-left"
                onClick={() => onManageSplit?.(transaction)}
                disabled={!onManageSplit}
            >
                <div className="flex items-center justify-between gap-3 text-foreground">
                    <div className="flex items-center gap-3">
                        <BsDiagram3 className="h-5 w-5 text-muted-foreground" />
                        <span className="text-base font-semibold">
                        {transaction.parentId === null ? transaction.subTransactions && transaction.subTransactions.length > 0
                                ? 'Part of split transaction'
                                : 'Split this Transaction'
                            : 'Part of split transaction'
                        }
                        </span>
                    </div>
                    <FiChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </div>
            </button>

            {transaction.emailMessageId && (
                <button
                    type="button"
                    onClick={() => onSourceEmailClick?.(transaction)}
                    className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-left"
                >
                    <div className="flex items-center justify-between gap-3 text-foreground">
                        <div className="flex items-center gap-3">
                            <FiMail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-base font-semibold">Source Email</span>
                        </div>
                        <FiChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                    </div>
                </button>
            )}

            {/* Note Section */}
            <div className="bg-secondary border border-border px-4 py-4 rounded-xl flex flex-col min-h-[150px]">
                <label htmlFor="transaction-note" className="mb-0 text-base font-semibold text-foreground">
                    Notes
                </label>
                <textarea
                    id="transaction-note"
                    className="w-full mt-2 bg-transparent text-foreground placeholder-muted-foreground resize-none focus:outline-none min-h-[80px]"
                    placeholder="Add a note for this transaction..."
                    value={note}
                    onChange={handleNoteChange}
                />
            </div>

            {/* Exclude from Cash Flow Section */}
            <div className="bg-secondary border border-border p-4 rounded-xl">
                <div className="flex items-center justify-between bg-input/60 border border-border/80 py-3 px-3 rounded-xl gap-2">
                    <div className="flex items-center">
                        <ExcludedIcon className="mr-2 h-6 w-6 text-foreground" />
                        <span className="text-foreground text-sm font-semibold">Exclude from Cash Flow</span>
                    </div>
                    {/* Simple Toggle Switch */}
                    <button
                        type="button"
                        onClick={toggleExclude}
                        className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary ${isExcluded ? 'bg-primary' : 'bg-input' // Use input color for off state
                            }`}
                    >
                        <span className="sr-only">Toggle Exclude from Cash Flow</span>
                        <span
                            className={`inline-block w-4 h-4 transform bg-background rounded-full transition-transform duration-200 ease-in-out ${isExcluded ? 'translate-x-6' : 'translate-x-1' // Adjusted translate values
                                }`}
                        />
                    </button>
                </div>
                <p className="text-muted-foreground text-sm mt-3 leading-relaxed">
                    Turn this on if you don't want this expense to affect Cash Flow calculations.
                </p>
            </div>
        </div>
    );
}

export default TransactionDetailView; 
